<script setup lang="ts">
import { cn } from '@/ui/lib/utils'
import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'

type OptionLike = RadioCardOption | string | number

const props = withDefaults(
  defineProps<{
    modelValue: OptionLike[]
    options: OptionLike[]
    iconAsRadio?: boolean
    class?: string
  }>(),
  { iconAsRadio: false, class: '' }
)

const emit = defineEmits<(e: 'update:modelValue', value: OptionLike[]) => void>()

const isObject = (o: unknown): o is RadioCardOption => typeof o === 'object' && o !== null

const sameOption = (a: OptionLike, b: OptionLike) => {
  if (a === b) return true
  if (isObject(a) && isObject(b)) {
    if (a.id != null && b.id != null) return a.id === b.id
    if (a.value !== undefined && b.value !== undefined) return a.value === b.value
  }
  return false
}

const isSelected = (option: OptionLike) => props.modelValue.some((value) => sameOption(value, option))

const toggle = (option: OptionLike) => {
  if (isObject(option) && option.disabled) return
  if (isSelected(option)) {
    emit(
      'update:modelValue',
      props.modelValue.filter((value) => !sameOption(value, option))
    )
  } else {
    emit('update:modelValue', [...props.modelValue, option])
  }
}

const getTitle = (o: OptionLike) => (isObject(o) ? o.title : undefined)
const getDescription = (o: OptionLike) => (isObject(o) ? o.description : undefined)
const getIcon = (o: OptionLike) => (isObject(o) ? o.icon : undefined)
const getDisabled = (o: OptionLike) => (isObject(o) ? !!o.disabled : false)

const wrapperClass = computed(() => props.class)
</script>

<template>
  <div :class="cn('grid gap-3', wrapperClass)">
    <div
      v-for="(option, index) in options"
      :key="isObject(option) && option.id != null ? option.id : index"
      role="checkbox"
      :aria-checked="isSelected(option)"
      :tabindex="getDisabled(option) ? -1 : 0"
      :class="[
        'group relative flex gap-3 rounded-xl border bg-card p-4 text-left outline-none transition-colors',
        isSelected(option) ? 'border-primary bg-primary/5' : '',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        getDescription(option) ? ' items-start' : 'items-center',
        getDisabled(option) ? 'cursor-default opacity-60' : 'cursor-pointer hover:border-primary'
      ]"
      @click="toggle(option)"
      @keydown.enter.prevent="toggle(option)"
      @keydown.space.prevent="toggle(option)"
    >
      <div
        v-if="iconAsRadio"
        class="flex size-10 items-center justify-center rounded-lg"
        :class="isSelected(option) ? 'bg-primary' : 'bg-muted'"
      >
        <BaseIcon
          v-if="getIcon(option)"
          :name="getIcon(option)!"
          class="aspect-square h-6 min-w-fit text-foreground-secondary"
          :class="isSelected(option) ? 'text-white' : ''"
        />
      </div>

      <div class="min-w-0 flex-1">
        <slot :name="index" :option="option" :checked="isSelected(option)">
          <div class="flex min-w-0 flex-col gap-1">
            <span v-if="getTitle(option)" class="truncate text-sm font-medium text-foreground">
              {{ getTitle(option) }}
            </span>
            <span v-if="getDescription(option)" class="text-xs text-muted-foreground">
              {{ getDescription(option) }}
            </span>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>
